/* eslint-disable react-hooks/exhaustive-deps */
import { TreeTypeEnum } from "@/common/enums";
import { BuildRCAttribute } from "@/common/function/RCAttribute";
import {
  Checkbox,
  CheckboxOnChangeData,
  Label,
} from "@fluentui/react-components";
import { cloneDeep } from "lodash";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ScrollDismissTooltip from "../Tooltip";
import { ITreeCheckboxProps, SMALL, TreeInfo } from "./Models";
import { TooltipWrapper } from "./Style";

const TreeCheckboxButton = (props: ITreeCheckboxProps) => {
  const { t } = useTranslation();
  const [nodeTree, setNodeTree] = useState<TreeInfo | null>(null);

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (nodeTree) {
      const nodeTemp = cloneDeep(props.node) as TreeInfo;
      nodeTemp.isCollapsed = nodeTree.isCollapsed;
      setNodeTree(nodeTemp);
    } else {
      setNodeTree(props.node);
    }
  }, [JSON.stringify(props.node)]);

  const onHandleCollapseBlock = (): void => {
    if (nodeTree) {
      const treeTemp = { ...nodeTree } as TreeInfo;
      treeTemp.isCollapsed = !treeTemp?.isCollapsed;
      setNodeTree(treeTemp);
    }
  };

  const onRenderLabel = (): JSX.Element => {
    if (nodeTree) {
      return (
        <div className="LabelCheckbox">
          {nodeTree.isUsedFloorPlan ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.66636 2.90524V11.2152L2.78657 13.2409C2.47476 13.4601 2.05227 13.263 2.00364 12.9015L1.99902 12.8318V5.74346C1.99902 5.60391 2.05726 5.47186 2.15777 5.37801L2.21148 5.33442L5.66636 2.90524ZM13.996 3.09845L14.0006 3.16811V10.2565C14.0006 10.396 13.9423 10.5281 13.8418 10.6219L13.7881 10.6655L10.333 13.0932V4.78257L13.213 2.75907C13.5248 2.53987 13.9473 2.73689 13.996 3.09845ZM6.66636 2.9059L9.33302 4.78057V13.0919L6.66636 11.2159V2.9059Z"
                fill="#106EBE"
              ></path>
            </svg>
          ) : null}
          <ScrollDismissTooltip relationship="label" content={nodeTree.name === TreeTypeEnum.Locations || TreeTypeEnum.Categories ? t(nodeTree.name) : nodeTree.name}>
            <TooltipWrapper $isSmall={props.size === SMALL}>
              <Label className="label_custom">{nodeTree.name === TreeTypeEnum.Locations || TreeTypeEnum.Categories ? t(nodeTree.name) : nodeTree.name}</Label>
            </TooltipWrapper>
          </ScrollDismissTooltip>
          {nodeTree.iconClosed}
        </div>
      );
    }
    return <Label className="LabelCheckbox">Locations</Label>;
  };

  const onHandleChangeCheckboxOnlyOneValue = (
    _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    dataChecked?: CheckboxOnChangeData,
    locationTree?: TreeInfo
  ): void => {
    if (dataChecked && dataChecked.checked && props.onChange && locationTree) {
      props.onChange([locationTree.guid]);
    } else {
      props.onChange && props.onChange([]);
    }
  };

  const onHandleChangeCheckbox = (
    _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    dataChecked?: CheckboxOnChangeData,
    locationTree?: TreeInfo
  ): void => {
    let values: string[] = props.values ? [...props.values] : [];
    switch (props.isSingle) {
      case true:
        if (
          dataChecked &&
          dataChecked.checked &&
          locationTree &&
          !values.includes(locationTree.guid)
        ) {
          if (locationTree.childDeep.length) {
            const ids: string[] = [
              locationTree.guid,
              ...locationTree.childDeep,
            ];
            values = [...new Set([...ids])];
          } else {
            values = [locationTree.guid];
          }
        } else if (
          dataChecked &&
          !dataChecked.checked &&
          locationTree &&
          values.includes(locationTree.guid)
        ) {
          values = values.filter(
            (item) =>
              item !== locationTree.guid &&
              !locationTree.childDeep.includes(item)
          );
        }
        if (props.onChange) {
          props.onChange(values, values[0]);
        }
        break;
      default:
        if (
          dataChecked &&
          dataChecked.checked &&
          locationTree &&
          !values.includes(locationTree.guid)
        ) {
          if (locationTree.childDeep.length) {
            const ids: string[] = [
              locationTree.guid,
              ...locationTree.childDeep,
            ];
            values = [...new Set([...values, ...ids])];
          } else {
            values.push(locationTree.guid);
            values = [...new Set([...values])];
          }
          if (
            props.mappingValueToListChild &&
            !values.includes(locationTree.parentId) &&
            locationTree.parentId
          ) {
            const childOfParent: string[] =
              props.mappingValueToListChild[locationTree.parentId] || [];
            const allYearsExist = childOfParent.every((id: string) =>
              values.includes(id)
            );
            if (
              childOfParent.length &&
              allYearsExist &&
              locationTree.parentId
            ) {
              values.push(String(locationTree.parentId));
              values = [...new Set([...values])];
            }
          }
        } else if (
          dataChecked &&
          !dataChecked.checked &&
          locationTree &&
          values.includes(locationTree.guid)
        ) {
          values = values.filter(
            (item) =>
              item !== locationTree.guid &&
              !locationTree.childDeep.includes(item)
          );
          if (props.isNormalLogic) {
            values = values.filter(
              (item) => !locationTree.parentIds.includes(item)
            );
          }
        }
        if (props.onChange) {
          props.onChange(values);
        }
        break;
    }
  };

  const checkParentHaveChildIsChecked = (listChild: string[]): boolean => {
    let isChecked = false;
    for (let index = 0; index < listChild.length; index++) {
      if (props.values && props.values.includes(listChild[index])) {
        isChecked = true;
        break;
      }
    }
    return isChecked;
  };

  const isChecked = (): boolean | "mixed" => {
    if (nodeTree && nodeTree.guid !== null) {
      return (props.values || []).includes(nodeTree.guid)
        ? true
        : checkParentHaveChildIsChecked(nodeTree.childDeep)
          ? "mixed"
          : false;
    }
    return false;
  };

  const isCheckedNotCheckbox = (): boolean => {
    if (nodeTree && nodeTree.guid !== null) {
      return (props.values || []).includes(nodeTree.guid) &&
        !(nodeTree.parentIds || []).some((parentId: string) =>
          props.values.includes(parentId)
        )
        ? true
        : false;
    }
    return false;
  };

  const onChangeSelectNotCheckbox = (): void => {
    let values: string[] = [];
    if (props.onChange && nodeTree) {
      if (nodeTree.childDeep.length) {
        const ids: string[] = [nodeTree.guid, ...nodeTree.childDeep];
        values = [...new Set([...ids])];
      } else {
        values = [nodeTree.guid];
      }
      props.onChange(values);
    }
  };

  const renderTreeNodeNotCheckbox = (): JSX.Element => {
    if (nodeTree) {
      return (
        <div
          className={`LabelCheckbox mlr-5 ${props.size === SMALL ? "size-small" : ""
            }`}
          onClick={nodeTree.isDisabled ? undefined : onChangeSelectNotCheckbox}
          style={{ opacity: nodeTree.isDisabled ? 0.5 : 1 }}
          {...BuildRCAttribute(
            `input_tree_select_list_item_not_ckb.${props.rcName}.${nodeTree.name}`
          )}
        >
          {nodeTree.isUsedFloorPlan ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.66636 2.90524V11.2152L2.78657 13.2409C2.47476 13.4601 2.05227 13.263 2.00364 12.9015L1.99902 12.8318V5.74346C1.99902 5.60391 2.05726 5.47186 2.15777 5.37801L2.21148 5.33442L5.66636 2.90524ZM13.996 3.09845L14.0006 3.16811V10.2565C14.0006 10.396 13.9423 10.5281 13.8418 10.6219L13.7881 10.6655L10.333 13.0932V4.78257L13.213 2.75907C13.5248 2.53987 13.9473 2.73689 13.996 3.09845ZM6.66636 2.9059L9.33302 4.78057V13.0919L6.66636 11.2159V2.9059Z"
                fill="#106EBE"
              ></path>
            </svg>
          ) : null}
          <ScrollDismissTooltip relationship="label" content={nodeTree.name === TreeTypeEnum.Locations || TreeTypeEnum.Categories ? t(nodeTree.name) : nodeTree.name}>
            <TooltipWrapper $isSmall={props.size === SMALL}>
              <Label className="label_custom">{nodeTree.name === TreeTypeEnum.Locations || TreeTypeEnum.Categories ? t(nodeTree.name) : nodeTree.name}</Label>
            </TooltipWrapper>
          </ScrollDismissTooltip>
          {nodeTree.iconClosed}
        </div>
      );
    }
    return <span className="LabelCheckbox">Locations</span>;
  };

  return (
    <>
      {nodeTree && nodeTree.guid !== null ? (
        <div className="TreeNodeWrapper">
          <div className="blk-item" ref={wrapperRef}>
            <div className="icon-isCollapsed">
              {nodeTree?.children?.length ? (
                <div
                  onClick={onHandleCollapseBlock}
                  style={{ cursor: "pointer" }}
                >
                  <svg
                    data-rc-id={`_tree-section.tree.node.expand.${!nodeTree.isCollapsed ? "open" : "close"
                      }.btn`}
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 24 24"
                    className={`rct-icon rct-icon-expand-close header__ico ${!nodeTree.isCollapsed ? "upIcon" : ""
                      }`}
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fill="none" d="M0 0h24v24H0z"></path>
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
                  </svg>
                </div>
              ) : null}
            </div>
            <div
              className={`ckb-custom ${props.isNotCheckbox && isCheckedNotCheckbox() ? "active" : ""
                }`}
              data-checked={
                props.isNotCheckbox ? isCheckedNotCheckbox() : isChecked()
              }
            >
              {props.isNotCheckbox ? (
                renderTreeNodeNotCheckbox()
              ) : (
                <Checkbox
                  className="form__checkbox"
                  checked={isChecked()}
                  onChange={(ev: FormEvent<HTMLElement | HTMLInputElement> | undefined, dataChecked: CheckboxOnChangeData | undefined) =>
                    !props.isOnlySelectOneValue
                      ? onHandleChangeCheckbox(ev, dataChecked, nodeTree)
                      : onHandleChangeCheckboxOnlyOneValue(
                        ev,
                        dataChecked,
                        nodeTree
                      )
                  }
                  label={onRenderLabel()}
                  {...BuildRCAttribute(
                    `input_tree_select_list_item.${props.rcName}.${nodeTree.name}`
                  )}
                />
              )}
            </div>
          </div>
          {!nodeTree.isCollapsed && nodeTree?.children?.length
            ? nodeTree?.children.map((node: TreeInfo) => (
              <div className="ml-12" key={node.guid}>
                <TreeCheckboxButton {...props} node={node} />
              </div>
            ))
            : null}
        </div>
      ) : null}
    </>
  );
};

export default TreeCheckboxButton;
